<template>
  <div class="settings">
    <div class="page-header-title mb-3">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h2 class="mb-1">{{ isCulturePage ? $t('settings.culture.title') : $t('settings.title') }}</h2>
          <ul class="breadcrumb mb-0">
            <li class="breadcrumb-item">
              <router-link to="/">{{ $t('common.navigation.home') || 'Home' }}</router-link>
            </li>
            <li class="breadcrumb-item" :class="{ active: !isCulturePage }">
              <router-link v-if="isCulturePage" to="/settings">{{ $t('settings.title') }}</router-link>
              <span v-else>{{ $t('settings.title') }}</span>
            </li>
            <li v-if="isCulturePage" class="breadcrumb-item active">{{ $t('settings.culture.title') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="row" v-if="isCulturePage">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">{{ $t('settings.culture.title') }}</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveGeneralSettings">
              <div class="card mb-3">
                <div class="card-header">
                  <h6 class="mb-0">{{ $t('settings.general.title') }}</h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <label for="locale" class="form-label">{{ $t('settings.general.locale') }}</label>
                      <select class="form-select locale-select" id="locale" v-model="settings.general.locale">
                        <option
                          v-for="loc in localeOptions"
                          :key="loc.code"
                          :value="loc.code"
                        >
                          {{ loc.label }}
                        </option>
                      </select>
                    </div>
                    <div class="col-12 col-md-6">
                      <label for="language" class="form-label">{{ $t('settings.general.language') }}</label>
                      <select class="form-select language-select" id="language" v-model="settings.general.language">
                        <option
                          v-for="lang in languageOptions"
                          :key="lang.code"
                          :value="lang.code"
                        >
                          {{ lang.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Disabled preview card for examples -->
              <div class="card example-card disabled mb-3">
                <div class="card-body py-2">
                  <div class="row g-2">
                    <div class="col-12 col-md-6">
                      <div class="small text-muted">{{ $t('settings.preview.selectedDateFormat') }}</div>
                      <div class="fw-semibold">{{ settings.general.dateFormat }}</div>
                    </div>
                    <div class="col-12 col-md-6">
                      <div class="small text-muted">{{ $t('settings.preview.dateTimePreview') }}</div>
                      <div class="fw-semibold">{{ dateExample }} • {{ dateTimeExample }}</div>
                    </div>
                    <div class="col-12 col-md-6">
                      <div class="small text-muted">{{ $t('settings.preview.numberPreview') }}</div>
                      <div class="fw-semibold">{{ numberExample }}</div>
                    </div>
                    <div class="col-12 col-md-6">
                      <div class="small text-muted">{{ $t('settings.preview.currencyPreview') }}</div>
                      <div class="fw-semibold">{{ currencyExample }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn btn-primary" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('common.actions.save') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-else>
      <div class="col-12">
        <div class="row">
          <div class="col-lg-6 col-xl-4 mb-4 mb-xl-0">
            <div class="card h-100">
              <div class="card-body d-flex flex-column justify-content-between">
                <div class="text-center text-muted">
                  <i class="ph-duotone ph-globe-hemisphere-west fa-3x mb-3 opacity-50"></i>
                  <h6 class="mb-2">{{ $t('settings.culture.title') }}</h6>
                  <p class="small mb-3">{{ $t('settings.culture.description') }}</p>
                </div>
                <div class="d-grid">
                  <router-link to="/culture" class="btn btn-outline-primary btn-sm">
                    {{ $t('settings.culture.open') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div
            class="col-lg-6 col-xl-4 mb-4 mb-xl-0"
            v-if="hasPdfTemplatesSettingsAccess"
          >
            <div class="card h-100">
              <div class="card-body d-flex flex-column justify-content-between">
                <div class="text-center text-muted">
                  <i class="fas fa-file-pdf fa-3x mb-3 opacity-50"></i>
                  <h6 class="mb-2">{{ $t('superAdmin.pdfTemplates.management') }}</h6>
                  <p class="small mb-3">{{ $t('superAdmin.pdfTemplates.description') }}</p>
                </div>
                <div class="d-grid">
                  <router-link to="/pdf-templates" class="btn btn-outline-primary btn-sm">
                    {{ $t('superAdmin.pdfTemplates.manage') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import authService from '@/services/authService';
import { useRoute } from 'vue-router';

export default {
  name: 'Settings',
  setup() {
    const store = useStore();
    const { locale: i18nLocale, t } = useI18n();
    const route = useRoute();

    const isCulturePage = computed(() => route.path === '/culture');

    const allowedPdfTemplatesEditionId = 'B0CB311A-A4D0-65B4-DF1B-39FF3E9F716E'
    const currentUser = computed(() => store.getters['auth/currentUser'])
    const hasAnyPermission = store.getters['auth/hasAnyPermission']
    const printTemplatePermissions = [
      'Supplier.PrintTemplates',
      'Supplier.PrintTemplates.Create',
      'Supplier.PrintTemplates.Edit',
      'Supplier.PrintTemplates.Delete'
    ]
    const hasPdfTemplatesSettingsAccess = computed(
      () =>
        currentUser.value?.editionId?.toUpperCase() === allowedPdfTemplatesEditionId &&
        hasAnyPermission(printTemplatePermissions)
    )
    
    const settings = ref({
      general: {
        dateFormat: 'DD/MM/YYYY',
        locale: localStorage.getItem('cultureLocale') || 'en-US',
        language: localStorage.getItem('locale') || 'en',
        // number formatting settings removed from UI; date format is derived from locale
      }
    });
    
    const isSaving = ref(false);

    // Language options for i18n (UI language)
    const languageOptions = computed(() => {
      return [
        { code: 'en', label: t('settings.languages.en') },
        { code: 'tr', label: t('settings.languages.tr') }
      ];
    });

    // Locale options with live examples
    const baseNumber = 12345.6789;
    const baseCurrency = 1999.9999; // Show 4 decimal places example
    const baseDate = new Date();

    const availableLocales = ref([
      { code: 'ar-AE', name: 'Arabic (United Arab Emirates)' },
      { code: 'ar-QA', name: 'Arabic (Qatar)' },
      { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
      { code: 'de-DE', name: 'German (Germany)' },
      { code: 'en-AE', name: 'English (United Arab Emirates)' },
      { code: 'en-GB', name: 'English (United Kingdom)' },
      { code: 'en-US', name: 'English (United States)' },
      { code: 'it-IT', name: 'Italian (Italy)' },
      { code: 'ja-JP', name: 'Japanese (Japan)' },
      { code: 'ms-SG', name: 'Malay (Singapore)' },
      { code: 'ru-RU', name: 'Russian (Russia)' },
      { code: 'tr-TR', name: 'Turkish (Türkiye)' },
      { code: 'ur-IN', name: 'Urdu (India)' }
    ]);

    const defaultDecimalPlaces = 2;
    const maxDecimalPlaces = 4;
    const defaultCurrency = 'USD';
    const defaultCurrencyDecimals = 2;
    const maxCurrencyDecimals = 4;
    const defaultUseGrouping = true;

    const localeOptions = computed(() => {
      return availableLocales.value.map(l => ({
        code: l.code,
        label: l.name
      }));
    });

    // Map locale to app date format string used by dayjs
    const localeToDateFormat = (loc) => {
      switch (loc) {
        case 'en-US':
          return 'MM/DD/YYYY';
        case 'en-GB':
        case 'en-AE':
        case 'ar-AE':
        case 'ar-QA':
        case 'it-IT':
          return 'DD/MM/YYYY';
        case 'ar-SA':
          return 'DD/MM/YY';
        case 'ms-SG':
          return 'D/MM/YYYY';
        case 'ru-RU':
          return 'DD.MM.YYYY';
        case 'tr-TR':
          return 'D.MM.YYYY';
        case 'de-DE':
          return 'DD.MM.YYYY';
        case 'ja-JP':
          return 'YYYY/MM/DD';
        case 'ur-IN':
          return 'D/M/YY';
        default:
          return 'YYYY-MM-DD';
      }
    };

    watch(() => settings.value.general.locale, (newLoc) => {
      settings.value.general.dateFormat = localeToDateFormat(newLoc);
    }, { immediate: true });

    const numberExample = computed(() => new Intl.NumberFormat(settings.value.general.locale, {
      minimumFractionDigits: defaultDecimalPlaces,
      maximumFractionDigits: maxDecimalPlaces,
      useGrouping: defaultUseGrouping
    }).format(baseNumber));

    const currencyExample = computed(() => new Intl.NumberFormat(settings.value.general.locale, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: defaultCurrencyDecimals,
      maximumFractionDigits: maxCurrencyDecimals
    }).format(baseCurrency));

    const dateExample = computed(() => baseDate.toLocaleDateString(settings.value.general.locale, { year: 'numeric', month: 'short', day: '2-digit' }));
    const dateTimeExample = computed(() => baseDate.toLocaleString(settings.value.general.locale, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }));
    
    // Watch language changes and update i18n
    watch(() => settings.value.general.language, (newLang) => {
      if (newLang && i18nLocale.value !== newLang) {
        i18nLocale.value = newLang;
        localStorage.setItem('locale', newLang);
      }
    }, { immediate: false });

    // Load settings from Vuex store
    onMounted(() => {
      const storeSettings = store.state.settings.general;
      settings.value.general = {
        ...storeSettings,
        language: localStorage.getItem('locale') || i18nLocale.value || 'en'
      };
      
      // Ensure i18n locale matches
      if (settings.value.general.language && i18nLocale.value !== settings.value.general.language) {
        i18nLocale.value = settings.value.general.language;
      }
    });

    // Save general settings
    const saveGeneralSettings = async () => {
      isSaving.value = true;

      try {
        // Update i18n locale if language changed
        if (settings.value.general.language && i18nLocale.value !== settings.value.general.language) {
          i18nLocale.value = settings.value.general.language;
          localStorage.setItem('locale', settings.value.general.language);
        }

        // Save culture settings (date/number/currency formatting)
        await store.dispatch('settings/updateGeneral', {
          locale: settings.value.general.locale,
          dateFormat: settings.value.general.dateFormat
        });

        // Persist culture to backend
        const userId = localStorage.getItem('userId');
        if (userId) {
          await authService.setUserCulture(userId, settings.value.general.locale);
        }
        store.dispatch('notify', {
          message: 'Settings saved successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('Error saving settings:', error);
        store.dispatch('notify', {
          message: 'Failed to save settings',
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };
    
    return {
      settings,
      isSaving,
      saveGeneralSettings,
      localeOptions,
      languageOptions,
      numberExample,
      currencyExample,
      dateExample,
      dateTimeExample,
      hasPdfTemplatesSettingsAccess,
      isCulturePage
    };
  }
};
</script>

<style scoped>
.page-header {
  margin-bottom: 1.5rem;
}

.theme-card, .language-card {
  cursor: pointer;
  transition: all 0.2s;
}

.theme-card.selected, .language-card.selected {
  border-color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.05);
}

/* Keep dropdown compact to avoid overly long width */
.date-format-select {
  max-width: 220px;
}
.language-select {
  max-width: 300px;
}
.locale-select {
  max-width: 480px;
}
.example-card.disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style> 